import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Icon, Popup, Form, Radio } from 'semantic-ui-react';
import cn from 'classnames';
import { Planning } from 'actions';
import { Button } from 'components';
import { colors } from 'utils/colors';

@connect((state) => ({
  portfolios: state.Portfolio.model.concat(state.Portfolio.user),
  drawdown: state.Planning.drawdown,
  cppDataset: state.Planning.cppDataset,
  oasDataset: state.Planning.oasDataset,
  accountList: state.Accounts.accountList,
  cppEstimator: state.Planning.cppEstimator,
}))
export default class InvestmentScenarios extends Component {
  state = {
    warning: {},
  };

  getPortfolio = (portfolios, portfolioId) =>
    portfolios.find((port) => port.id === Number(portfolioId));

  submit = () => {
    const { dispatch, drawdown, portfolios } = this.props;
    const { inputs, errors } = drawdown;

    const validated = Object.keys(inputs).reduce(
      (acc, val) => {
        if (val === 'constantRets' && !inputs.isRiskFree) {
          return acc;
        } else if (val === 'constantRets' && inputs.isRiskFree) {
          if (Number(inputs[val]) <= 0) {
            return acc;
          } else if (inputs[val].toString().split('.')[1]) {
            acc[val] = Number.parseFloat(inputs[val]).toFixed(2);
            return acc;
          }
        }

        if (inputs[val] !== '') {
          acc[val] = inputs[val];
        }

        return acc;
      },
      {
        rrifAge: inputs.cppAge || 0,
      }
    );

    const required = [
      'age',
      'horizon',
      'endValue',
      'prov',
      'cppAge',
      'oasAge',
      'cpp',
      'oas',
      'initRegular',
      !inputs.isRiskFree ? 'pctCapGain' : '',
      !inputs.isRiskFree ? 'divYield' : '',
      !inputs.isRiskFree ? 'pctEligibleDiv' : '',
      'initRRIF',
      'initTFSA',
      !inputs.isRiskFree ? 'portfolio' : 'constantRets',
      !!Number(inputs.other) && 'otherHorizon',
      !inputs.isRiskFree ? 'scenario' : null,
    ];

    const group = {
      generalInformation: ['age', 'horizon', 'prov'],
      retirementIncome: ['cppAge', 'oasAge', 'cpp', 'oas', 'otherHorizon'],
      investmentPortfolio: ['constantRets', 'portfolio'],
      portfolioValues: [
        'initRegular',
        'pctCapGain',
        'divYield',
        'pctEligibleDiv',
        'initRRIF',
        'initTFSA',
        'endValue',
      ],
      investmentSecnarios: ['scenario'],
    };

    const groupOrder = [
      'generalInformation',
      'retirementIncome',
      'investmentPortfolio',
      'portfolioValues',
      'investmentSecnarios',
    ];

    const maxCPPAge = inputs.prov === 'QC' ? 72 : 70;

    const errorObject = required.reduce((acc, val) => {
      if (val && !validated[val]) {
        if (
          (val === 'endValue' &&
            validated[val] !== 0 &&
            validated[val] !== undefined &&
            validated[val] !== null &&
            validated[val] !== '') ||
          (val === 'initRegular' && validated[val] === 0) ||
          (val === 'initRRIF' && validated[val] === 0) ||
          (val === 'initTFSA' && validated[val] === 0)
        ) {
          return acc;
        }
        acc[val] = true;
      }

      if (val === 'age' && Number(validated[val]) < 49) {
        acc[val] = '(cannot be less than 49)';
      }

      if (val === 'cpp') {
        if (typeof errors.cpp === 'string') {
          acc[val] = errors.cpp;
        }
      }

      if (val === 'oas') {
        if (typeof errors.oas === 'string') {
          acc[val] = errors.oas;
        }
      }

      if (val === 'cppAge') {
        if (inputs.cppStarted) {
          if (Number(validated[val]) < 60) {
            acc[
              val
            ] = `You can only start collecting CPP/QPP benefits between age 60 and age ${maxCPPAge}.`;
          }
        } else if (!inputs.cppStarted) {
          if (
            Number(validated[val] > maxCPPAge || Number(validated[val]) < 60)
          ) {
            acc[
              val
            ] = `You can only start collecting CPP/QPP benefits between age 60 and age ${maxCPPAge}.`;
          }
        }
      }

      if (val === 'oasAge') {
        if (inputs.oasStarted) {
          if (Number(validated[val]) < 65) {
            acc[val] =
              'You can only start collecting OAS benefits between age 65 and age 70.';
          }
        } else if (!inputs.oasStarted) {
          if (Number(validated[val] > 70 || Number(validated[val]) < 65)) {
            acc[val] =
              'You can only start collecting OAS benefits between age 65 and age 70.';
          }
        }
      }

      if (val === 'horizon' && Number(validated[val]) < 1) {
        acc[val] = '(cannot be less than 1)';
      }

      if (
        val === 'otherHorizon' &&
        Number(validated[val]) > Number(validated.horizon || 0)
      ) {
        acc[val] = '(cannot be longer than your planning horizon)';
      }

      if (
        val === 'initRegAccList' &&
        (!inputs.initRegAccList || !inputs.initRegAccList.length)
      ) {
        acc.initRegular = true;
      }

      if (
        val === 'initRRIFAccList' &&
        (!inputs.initRRIFAccList || !inputs.initRRIFAccList.length)
      ) {
        acc.initRRIF = true;
      }

      if (
        val === 'initTFSAAccList' &&
        (!inputs.initTFSAAccList || !inputs.initTFSAAccList.length)
      ) {
        acc.initTFSA = true;
      }

      return acc;
    }, {});

    const hasError = Object.values(errorObject).find((val) => val);

    if (hasError) {
      let i = 0;
      for (; i < groupOrder.length; i += 1) {
        let leaveLoop = false;
        for (let j = 0; j < group[groupOrder[i]].length; j += 1) {
          if (group[groupOrder[i]][j] in errorObject) {
            leaveLoop = true;
            break;
          }
        }

        if (leaveLoop) {
          break;
        }
      }

      return dispatch(
        Planning.drawdownUpdateInput({
          inputs: {},
          errors: errorObject,
          modified: true,
        })
      );
    }

    if (inputs.cppStarted) {
      if (!inputs.cppAge) {
        dispatch(
          Planning.drawdownUpdateInput({
            inputs: { cppAge: inputs.age },
            modified: true,
          })
        );
      }
    }

    if (inputs.oasStarted) {
      if (!inputs.oasAge) {
        dispatch(
          Planning.drawdownUpdateInput({
            inputs: { oasAge: inputs.age },
            modified: true,
          })
        );
      }
    }

    const selectedPortfolio = this.getPortfolio(portfolios, inputs.portfolio);

    const portfolio = !inputs.isRiskFree
      ? {
          isRiskFree: false,
          portId: selectedPortfolio.id,
          symbols: selectedPortfolio.symbols,
          weights: selectedPortfolio.weights,
          gic_info: selectedPortfolio.gic_info,
        }
      : {
          constantRets: Number(validated.constantRets) / 100,
          isRiskFree: true,
        };

    dispatch(
      Planning.drawdownUpdateInput({
        inputs: validated,
        errors: {},
        modified: false,
      })
    );
    dispatch(Planning.updateJustsaveOrFetch(false));

    return dispatch(
      Planning.calcDrawdown({
        ...validated,
        ...portfolio,
        init_date: portfolio.init_date,
        otherHorizon: Number(validated.otherHorizon) || 0,
        divYield: inputs.isRiskFree ? 0 : Number(validated.divYield) / 100,
        pctCapGain: inputs.isRiskFree ? 0 : Number(validated.pctCapGain) / 100,
        pctEligibleDiv: inputs.isRiskFree
          ? 0
          : Number(validated.pctEligibleDiv) / 100,
        pensionInf: validated.pensionInf,
        scenario: validated.scenario,
      })
    ).then(() => {
      dispatch(
        Planning.setScenario(!inputs.isRiskFree ? validated.scenario : '')
      );
      dispatch(Planning.navigateDrawdown(6));
    });
  };

  changeSelection = (field, value) => {
    const { drawdown, dispatch } = this.props;

    if (field === 'isRiskFree') {
      return dispatch(
        Planning.drawdownUpdateInput({
          inputs: {
            [field]: value,
            pctCapGain: 0,
            divYield: 0,
            pctEligibleDiv: 0,
            ...(!value && { portfolio: drawdown.inputs.portfolio }),
          },
          errors: {
            pctCapGain: false,
            divYield: false,
            pctEligibleDiv: false,
            portfolio: false,
            constantRets: false,
          },
          modified: true,
        })
      );
    }

    if (field === 'cppStarted') {
      if (drawdown.inputs.cppStarted !== value) {
        dispatch(
          Planning.drawdownUpdateInput({
            inputs: { [field]: value, cppAge: '', cpp: '' },
            errors: { cppAge: false, cpp: false },
            modified: true,
          })
        );

        return this.setState({ warning: { cpp: '' } });
      }
    }

    if (field === 'oasStarted') {
      if (drawdown.inputs.oasStarted !== value) {
        dispatch(
          Planning.drawdownUpdateInput({
            inputs: { [field]: value, oasAge: '', oas: '' },
            errors: { oasAge: false, oas: false },
            modified: true,
          })
        );

        return this.setState({ warning: { oas: '' } });
      }
    }

    return dispatch(
      Planning.drawdownUpdateInput({
        inputs: { [field]: value },
        errors: {},
        modified: true,
      })
    );
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    const { dispatch, drawdown, cppDataset, oasDataset, cppEstimator } =
      this.props;
    const { inputs } = drawdown;
    let overMaxPrompt = null;
    let money = null;
    let max = Number.MIN_SAFE_INTEGER;

    const parsedValue = (field, val) => {
      const currency = [
        'cpp',
        'oas',
        'pension',
        'other',
        'initRegular',
        'initRRIF',
        'initTFSA',
        'endValue',
      ];

      if (field === 'horizon') {
        const maxHorizon = 80;
        if (Number(val) >= maxHorizon) {
          // Display an error message for horizon exceeding the maximum
          this.setState({
            warning: {
              ...this.state.warning,
              horizon:
                'Please input the number of years you are expected to be retired for, rather than the age you expect to live until.',
            },
          });
          return maxHorizon;
        }
      }

      if (currency.indexOf(field) === -1 && Number(val) > 100) {
        return 100;
      }

      return val;
    };

    const setAge = (age, isOas) => {
      if (!age) {
        return 70;
      }

      if (isOas && Number(age) < 65) {
        return 65;
      }

      if (Number(age) < 60) {
        return 60;
      } else if (Number(age) > 70) {
        return 70;
      }

      return age;
    };

    if (name === 'age') {
      const cppMax = cppDataset.find(
        (item) =>
          item.amount_type === 'maximum' &&
          item.pension_name === 'CPP' &&
          Number(item.age) === Number(setAge(parsedValue(name, value), false))
      ).amount;
      const oasMax = oasDataset.find(
        (item) =>
          item.amount_type === 'maximum' &&
          item.pension_name === 'OAS' &&
          Number(item.age) === Number(setAge(parsedValue(name, value), true))
      ).amount;
      if (Number(value) < 60) {
        this.changeSelection('cppStarted', false);
        this.changeSelection('oasStarted', false);
      } else if (Number(value) < 65) {
        this.changeSelection('oasStarted', false);
        this.changeSelection('cppStarted', true);
      } else {
        this.changeSelection('oasStarted', true);
        this.changeSelection('cppStarted', true);
      }
      dispatch(
        Planning.drawdownUpdateInput({
          inputs: {
            age: parsedValue(name, value),
            ...(Number(value) > 60 && { cppAge: parsedValue(name, value) }),
            ...(Number(value) > 65 && { oasAge: parsedValue(name, value) }),
          },
          errors: {
            age: false,
            ...(Number(value) > 60 && inputs.cppAge && { cppAge: false }),
            ...(Number(value) > 65 && inputs.oasAge && { oasAge: false }),
            // ...(inputs.cppStarted &&
            //   inputs.cpp > cppMax && {
            //     cpp:
            //       'The benefit amount you entered exceeded the maximum for your current age.',
            //   }),
            ...(inputs.oasStarted &&
              inputs.oas > oasMax && {
                oas: 'The benefit amount you entered exceeded the maximum for your current age.',
              }),
            ...(inputs.cppStarted && inputs.cpp <= cppMax && { cpp: null }),
            ...(inputs.oasStarted && inputs.oas <= oasMax && { oas: null }),
            ...(!inputs.cppStarted && { cppAge: null }),
            ...(!inputs.oasStarted && { oasAge: null }),
          },
          modified: true,
        })
      );
    }

    if (name === 'cppAge') {
      return dispatch(
        Planning.drawdownUpdateInput({
          inputs: { cppAge: parsedValue(name, value), cpp: '' },
          errors: { cppAge: false, cpp: false },
          modified: true,
        })
      );
    }

    if (name === 'oasAge') {
      return dispatch(
        Planning.drawdownUpdateInput({
          inputs: { oasAge: parsedValue(name, value), oas: '' },
          errors: { oasAge: false, oas: false },
          modified: true,
        })
      );
    }

    if (name === 'cpp') {
      if (!inputs.cppAge && inputs.cppStarted) {
        dispatch(
          Planning.drawdownUpdateInput({
            inputs: { cppAge: inputs.age || 0 },
            modified: true,
          })
        );
      }

      const currentYear = new Date().getFullYear();

      const body = {
        retirement_year:
          Number(currentYear) + Number(inputs.cppAge) - Number(inputs.age),
        start_cpp_age: inputs.cppAge,
        years_contrib: '45',
        primary_caregiver: false,
        years_primary_caregiver: 0,
        is_earning_over_average: true,
        is_earning_14pct_higher: true,
      };
      if (inputs.cppAge !== '' && inputs.cppAge >= 60) {
        dispatch(Planning.calcCpp({ ...body }));
        max = cppEstimator.result;

        if (value > max && max > 100) {
          money = max;
          overMaxPrompt = `This value is the maximum annual benefits for your CPP/QPP start age and year.`;
        } else {
          money = value;
        }

        this.setState({
          warning: { ...this.state.warning, cpp: overMaxPrompt },
        });

        return dispatch(
          Planning.drawdownUpdateInput({
            inputs: { cpp: money },
            errors: { cpp: false },
            modified: true,
          })
        );
      }
    }

    if (name === 'oas') {
      if (!inputs.oasAge && inputs.oasStarted) {
        dispatch(
          Planning.drawdownUpdateInput({
            inputs: { oasAge: inputs.age || 0 },
            modified: true,
          })
        );
      }

      if (!inputs.oasAge || inputs.oasAge > 70) {
        max = oasDataset.find(
          (item) =>
            item.amount_type === 'maximum' &&
            item.pension_name === 'OAS' &&
            item.age === 70
        ).amount;
      } else if (inputs.oasAge < 65) {
        max = oasDataset.find(
          (item) =>
            item.amount_type === 'maximum' &&
            item.pension_name === 'OAS' &&
            item.age === 65
        ).amount;
      } else {
        max = oasDataset.find(
          (item) =>
            item.amount_type === 'maximum' &&
            item.pension_name === 'OAS' &&
            item.age === Number(inputs.oasAge)
        ).amount;
      }

      if (value > max) {
        money = max;
        overMaxPrompt = `This value is the maximum annual benefits for age ${setAge(
          inputs.oasAge,
          true
        )}.`;
      } else {
        money = value;
      }

      this.setState({
        warning: { ...this.state.warning, oas: overMaxPrompt },
      });

      return dispatch(
        Planning.drawdownUpdateInput({
          inputs: { oas: money },
          errors: { oas: false },
          modified: true,
        })
      );
    }

    if (name === 'other') {
      return dispatch(
        Planning.drawdownUpdateInput({
          inputs: { other: parsedValue(name, value) },
          errors: { otherHorizon: false },
          modified: true,
        })
      );
    }

    return dispatch(
      Planning.drawdownUpdateInput({
        inputs: { [name]: parsedValue(name, value) },
        errors: { [name]: false },
        modified: true,
      })
    );
  };

  hasErrors = () => {
    const { errors } = this.props.drawdown;
    return Object.values(errors).some((error) => error);
  };

  portfolioIsDeleted = (inputs, portfolios) => {
    if (inputs.isRiskFree === undefined) return false;
    if (inputs.isRiskFree) return false;
    if (inputs.portfolio === undefined) return false;

    return !portfolios.find((p) => p.id === inputs.portfolio);
  };

  handleRadioChange = (e, { value, name }) => {
    this.setState({ [name]: value });
    this.props.dispatch(
      Planning.drawdownUpdateInput({
        inputs: { [name]: value },
        errors: { [name]: false },
        modified: true,
      })
    );

    switch (name) {
      case 'initRegularFromAcc':
        this.props.dispatch(
          Planning.drawdownUpdateInput({
            inputs: { initRegular: null, initRegAccList: [] },
            modified: true,
          })
        );
        break;
      case 'initRRIFFromAcc':
        this.props.dispatch(
          Planning.drawdownUpdateInput({
            inputs: { initRRIF: null, initRRIFAccList: [] },
            modified: true,
          })
        );
        break;
      case 'totalTfsaFromAcc':
        this.props.dispatch(
          Planning.drawdownUpdateInput({
            inputs: { initTFSA: null, initTFSAAccList: [] },
            modified: true,
          })
        );
        break;
      default:
    }
  };

  render() {
    const { inputs, isFetching } = this.props.drawdown;
    const { portfolios } = this.props;
    const { scenario } = this.props;

    return (
      <div>
        {!inputs.isRiskFree ? (
          <div>
            <Section single>
              <div className="title">
                We will run 10,000 simulations using your investment portfolio's
                historical returns.
              </div>
              <div style={{ marginLeft: '1rem' }}>
                <Form onSubmit={this.submit}>
                  <Form.Field>
                    <Radio
                      name="scenario"
                      value="median"
                      checked={scenario === 'median'}
                      onChange={this.handleRadioChange}
                      label="Show me the median outcome."
                    />
                  </Form.Field>
                  <Form.Field>
                    <Radio
                      name="scenario"
                      value="worst3"
                      checked={scenario === 'worst3'}
                      onChange={this.handleRadioChange}
                      label="Show me a poor outcome with sequence of returns risk."
                    />
                    <Popup
                      trigger={<Icon name="question circle outline" />}
                      position="top center"
                      wide
                      content="This scenario assumes that in the first three years of the planning horizon, the portfolio experiences the three worst annualized returns (beginning with the lowest in year 1) based on 10,000 simulations of historical monthly return paths."
                    />
                  </Form.Field>
                </Form>
              </div>
            </Section>
            {this.hasErrors() && (
              <div
                style={{
                  color: `${colors.red}`,
                  marginTop: '2rem',
                  fontWeight: 'bold',
                }}
              >
                Please ensure all your inputs are complete before generating
                your plan.
              </div>
            )}
            <Button
              className={cn('left', { loading: isFetching })}
              color={colors.blue}
              icon="calculator"
              onClick={this.submit}
              style={{
                margin: '1rem 0',
                position: 'absolute',
                zIndex: '3',
                bottom: '15px',
                right: '25px',
                opacity: this.portfolioIsDeleted(inputs, portfolios)
                  ? '0.45'
                  : '1',
              }}
              disabled={this.portfolioIsDeleted(inputs, portfolios)}
            >
              Calculate
            </Button>
          </div>
        ) : (
          <div>
            <Section single>
              <div className="title">
                Since you chose to put your retirement savings in a risk free
                investment, your investment scenario can be calculated with
                certainty. Please click "Calculate" to proceed.
              </div>
            </Section>
            {this.hasErrors() && (
              <div
                style={{
                  color: `${colors.red}`,
                  marginTop: '2rem',
                  fontWeight: 'bold',
                }}
              >
                Please ensure all your inputs are complete before generating
                your plan.
              </div>
            )}
            <Button
              className={cn('left', { loading: isFetching })}
              color={colors.blue}
              icon="calculator"
              onClick={this.submit}
              style={{
                margin: '1rem 0',
                position: 'absolute',
                zIndex: '3',
                bottom: '15px',
                right: '25px',
                opacity: this.portfolioIsDeleted(inputs, portfolios)
                  ? '0.45'
                  : '1',
              }}
              disabled={this.portfolioIsDeleted(inputs, portfolios)}
            >
              Calculate
            </Button>
          </div>
        )}
      </div>
    );
  }
}

InvestmentScenarios.propTypes = {
  dispatch: PropTypes.func,
  portfolios: PropTypes.array,
  drawdown: PropTypes.object,
  cppDataset: PropTypes.array,
  oasDataset: PropTypes.array,
  cppEstimator: PropTypes.object,
  scenario: PropTypes.string,
};

InvestmentScenarios.defaultProps = {
  dispatch: () => false,
  portfolios: [],
  drawdown: {},
  cppDataset: [],
  oasDataset: [],
  cppEstimator: {},
  scenario: '',
};

const Section = styled.section`
  display: flex; /* Add this line */
  flex-direction: column;
  padding-left: 2rem;
  position: relative;
  text-align: left;
  line-height: 1.5rem;
  max-width: ${(props) => (props.single ? '52rem' : '100%')};
  transition: all 200ms ease;

  &.invalid {
    border: 1px solid ${colors.red};
  }

  .heading {
    position: absolute;
    top: -19px;
    left: 20px;
    padding: 0.5rem;
    background: white;
    font-size: 1.28em;
    font-weight: 700;
  }

  .title {
    font-size: 1.2rem;
    margin: 1rem 0;
    display: inline-block;
  }

  ul {
    position: relative;
    list-style: none;
    margin: 0;
    padding: 0;

    li {
      position: relative;
      clear: both;
      height: 0;
      opacity: 0;
      overflow: hidden;
      line-height: 15px;
      transition: all 200ms ease;

      &.visible {
        height: 35px;
        opacity: 1;
      }
    }

    label {
      font-size: 1rem;
      display: inline-block;
      padding-left: 1rem;
      padding-top: 5px;
    }
  }

  .description {
    display: inline-block;
    font-size: 1rem;
    padding: 3px 0 0.7rem 1rem;
    line-height: 1rem;
    width: 61%;
    clear: both;
  }

  .disabled {
    pointer-events: none;
    opacity: 0.4;
  }
`;
